import type { QueryClient } from '@tanstack/react-query'
import {
  getMeEmailsCreateMutationOptions,
  getMeEmailsListQueryKey,
  getOrganizationsInvitesCreateMutationOptions,
  getOrganizationsInvitesDestroyMutationOptions,
  getOrganizationsInvitesListQueryKey,
  getOrganizationsInvitesPartialUpdateMutationOptions,
  getOrganizationsInvitesRetrieveQueryKey,
  getOrganizationsListQueryKey,
  getOrganizationsMembersDestroyMutationOptions,
  getOrganizationsMembersListQueryKey,
  getOrganizationsMembersPartialUpdateMutationOptions,
  getOrganizationsMembersRetrieveQueryKey,
  getOrganizationsPartialUpdateMutationOptions,
  getOrganizationsRetrieveQueryKey,
  type organizationsInvitesRetrieveResponse,
  type organizationsMembersListResponse,
  type organizationsMembersRetrieveResponse,
} from '#/api/react-query/user-team-organization-usage'
import profile from '#/stores/profile'
import { getAssetUIDFromUrl } from '#/utils'
import {
  invalidateItem,
  invalidatePaginatedList,
  optimisticallyUpdateItem,
  optimisticallyUpdatePaginatedList,
} from './common'

/**
 * Applies user-team-organization-usage mutation defaults to a QueryClient instance.
 * Call this on both the global queryClient and test/story queryClients
 * to ensure consistent invalidation behavior.
 */
export function applyUserTeamOrganizationMutationDefaults(client: QueryClient): void {
  client.setMutationDefaults(
    getOrganizationsPartialUpdateMutationOptions().mutationKey!,
    getOrganizationsPartialUpdateMutationOptions({
      /**
       * Good simple example for a "invalidate only" approach.
       * Note that it's possible to optimistically update both the list and item (like below), but doesn't.
       */
      mutation: {
        onSettled: (_data, _error, variables) => {
          invalidatePaginatedList(getOrganizationsListQueryKey(), client)
          invalidateItem(getOrganizationsRetrieveQueryKey(variables.uidOrganization), client)
        },
      },
    }),
  )

  client.setMutationDefaults(
    getOrganizationsInvitesCreateMutationOptions().mutationKey!,
    getOrganizationsInvitesCreateMutationOptions({
      /**
       * Good simple example for a "invalidate only" approach.
       * Note that:
       * - when creating an item then no need to invalidate any of existing items.
       * - when creating an item then invalidate member list as well, because KPI placeholds members based on invites.
       *
       * Also a good example when NOT to use optimistic updates, because:
       * - in current UI it would gain nothing to insert the single item cache
       * - it's impossible to guess the order of items in the lists, so don't even try
       */
      mutation: {
        onSettled: (_data, _error, variables) => {
          invalidatePaginatedList(getOrganizationsInvitesListQueryKey(variables.uidOrganization), client)
          invalidatePaginatedList(getOrganizationsMembersListQueryKey(variables.uidOrganization), client)
        },
      },
    }),
  )
  client.setMutationDefaults(
    getOrganizationsInvitesDestroyMutationOptions().mutationKey!,
    getOrganizationsInvitesDestroyMutationOptions({
      /**
       * Good complex example for optimistic update approach.
       * Note that members are placeholded based on invites, thus need to handle list for both invites and members.
       * When dealing with more than one list and/or item, better name then specifically.
       */
      mutation: {
        onMutate: async ({ guid, uidOrganization }) => {
          // Note: `useOrganizationsInvitesList` is unused, skipping optimistically updating it.
          const invitesSnapshots: [readonly unknown[], unknown][] = []

          const membersSnapshots = await optimisticallyUpdatePaginatedList<organizationsMembersListResponse>(
            getOrganizationsMembersListQueryKey(uidOrganization),
            (response) =>
              ({
                ...response,
                data: {
                  ...response?.data,
                  ...(response?.status === 200
                    ? {
                        results: response?.data.results.filter(
                          ({ invite }) => !invite?.url || getAssetUIDFromUrl(invite?.url) !== guid,
                        ),
                      }
                    : {}),
                },
              }) as organizationsMembersListResponse,
            client,
          )

          const inviteSnapshot = await optimisticallyUpdateItem(
            getOrganizationsInvitesRetrieveQueryKey(uidOrganization, guid),
            null,
            client,
          )

          return {
            snapshots: [...invitesSnapshots, ...membersSnapshots, inviteSnapshot],
          }
        },
      },
    }),
  )
  client.setMutationDefaults(
    getOrganizationsInvitesPartialUpdateMutationOptions().mutationKey!,
    getOrganizationsInvitesPartialUpdateMutationOptions({
      /**
       * Good complex example for optimistic update approach.
       * Note that members are placeholded based on invites, thus need to handle list for both invites and members.
       * When dealing with more than one list and/or item, better name them specifically.
       */
      mutation: {
        onMutate: async ({ uidOrganization, guid, data }) => {
          // Note: `useOrganizationsInvitesList` is unused, skipping optimistically updating it.
          const invitesSnapshots: [readonly unknown[], unknown][] = []

          const membersSnapshots = await optimisticallyUpdatePaginatedList<organizationsMembersListResponse>(
            getOrganizationsMembersListQueryKey(uidOrganization),
            (response) =>
              ({
                ...response,
                data: {
                  ...response?.data,
                  ...(response?.status === 200
                    ? {
                        results: response?.data.results.map((member) => ({
                          ...member,
                          invite: member.invite
                            ? {
                                ...member.invite,
                                invitee_role:
                                  'role' in data && getAssetUIDFromUrl(member.invite.url) === guid
                                    ? data.role
                                    : member.invite.invitee_role,
                                status:
                                  'status' in data && getAssetUIDFromUrl(member.invite.url) === guid
                                    ? data.status
                                    : member.invite.status,
                              }
                            : member.invite,
                        })),
                      }
                    : {}),
                },
              }) as organizationsMembersListResponse,
            client,
          )

          const inviteSnapshot = await optimisticallyUpdateItem<organizationsInvitesRetrieveResponse>(
            getOrganizationsInvitesRetrieveQueryKey(uidOrganization, guid),
            (response) =>
              ({
                ...response,
                data: {
                  ...response?.data,
                  ...(response?.status === 200
                    ? {
                        ...response?.data,
                        ...('role' in data ? { invitee_role: data.role } : null),
                        ...('status' in data ? { status: data.status } : null),
                      }
                    : {}),
                },
              }) as organizationsInvitesRetrieveResponse,
            client,
          )

          return {
            snapshots: [...invitesSnapshots, ...membersSnapshots, inviteSnapshot],
          }
        },
      },
    }),
  )

  client.setMutationDefaults(
    getOrganizationsMembersDestroyMutationOptions().mutationKey!,
    getOrganizationsMembersDestroyMutationOptions({
      /**
       * Good simple example for optimistic update approach. Note that usually have to handle 1 list and 1 item.
       */
      mutation: {
        onMutate: async ({ uidOrganization, username }) => {
          const listSnapshots = await optimisticallyUpdatePaginatedList<organizationsMembersListResponse>(
            getOrganizationsMembersListQueryKey(uidOrganization),
            (response) =>
              ({
                ...response,
                data: {
                  ...response?.data,
                  ...(response?.status === 200
                    ? {
                        results: response?.data.results.filter((member) => member.user__username !== username),
                      }
                    : {}),
                },
              }) as organizationsMembersListResponse,
            client,
          )

          const memberSnapshot = await optimisticallyUpdateItem<organizationsMembersRetrieveResponse>(
            getOrganizationsMembersRetrieveQueryKey(uidOrganization, username),
            null,
            client,
          )

          return {
            snapshots: [...listSnapshots, memberSnapshot],
          }
        },
        onSuccess: (_data, { username }) => {
          // If user is removing themselves, we need to clear the session
          if (username === profile.currentAccount?.username) profile.refreshAccount()
        },
      },
    }),
  )
  client.setMutationDefaults(
    getOrganizationsMembersPartialUpdateMutationOptions().mutationKey!,
    getOrganizationsMembersPartialUpdateMutationOptions({
      /**
       * Good simple example for optimistic update approach. Note that usually have to handle 1 list and 1 item.
       */
      mutation: {
        onMutate: async ({ uidOrganization, username, data: { role } }) => {
          if (!role) return

          const listSnapshots = await optimisticallyUpdatePaginatedList<organizationsMembersListResponse>(
            getOrganizationsMembersListQueryKey(uidOrganization),
            (response) =>
              ({
                ...response,
                data: {
                  ...response?.data,
                  ...(response?.status === 200
                    ? {
                        results: response?.data.results.map((member) => ({
                          ...member,
                          role: member.user__username === username ? role : member.role,
                        })),
                      }
                    : {}),
                },
              }) as organizationsMembersListResponse,
            client,
          )

          const memberSnapshot = await optimisticallyUpdateItem<organizationsMembersRetrieveResponse>(
            getOrganizationsMembersRetrieveQueryKey(uidOrganization, username),
            (response) =>
              ({
                ...response,
                data: {
                  ...response?.data,
                  ...(response?.status === 200 ? { role: role } : {}),
                },
              }) as organizationsMembersRetrieveResponse,
            client,
          )

          return {
            snapshots: [...listSnapshots, memberSnapshot],
          }
        },
      },
    }),
  )

  client.setMutationDefaults(
    getMeEmailsCreateMutationOptions().mutationKey!,
    getMeEmailsCreateMutationOptions({
      // This POST drops any other unverified address while adding the new one, so a refetch is simpler than keeping a
      // snapshot in step with it. `onSettled` for the reason in `useRemoveUnverifiedEmails`.
      mutation: {
        onSettled: () => {
          client.invalidateQueries({ queryKey: getMeEmailsListQueryKey() })
        },
      },
    }),
  )
}
